import React from 'react';
import { ArrowRight, Users, Target, Globe, Star, CheckCircle, Phone, Mail, MapPin } from 'lucide-react';
import ResponsiveNavbar from './ResponsiveNavbar';

const HomePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Navigation */}
     <ResponsiveNavbar/>

      {/* Hero Section */}
      <section id="home" className="pt-20 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
              🚀 Your Trusted Consulting Partner
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Empowering Your
              <span className="bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent"> Success</span>
              <br />Through Expert Guidance
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              Welcome to SmeHub Consulting Limited - your gateway to educational consulting, immigration services, 
              and comprehensive business solutions that drive tangible results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href='/service' className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-8 py-4 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center justify-center">
                Get Started Today
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
              <a href='/about-us' className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-xl font-semibold hover:border-blue-500 hover:text-blue-600 transition-all duration-300 flex items-center justify-center">
                Learn More
              </a>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20">
            {[
              { number: '500+', label: 'Students Enrolled' },
              { number: '20+', label: 'Years Experience' },
              { number: '98%', label: 'Success Rate' },
              { number: '50+', label: 'Partner Institutions' }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Why Choose SmeHub?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We combine innovation, collaboration, and expertise to deliver exceptional results
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Target className="w-8 h-8" />,
                title: 'Innovation',
                description: 'We embrace the latest technologies and methodologies to deliver cutting-edge solutions that drive innovation and foster growth.'
              },
              {
                icon: <Users className="w-8 h-8" />,
                title: 'Collaboration',
                description: 'We believe in the power of partnership, working closely with clients to foster open communication and mutual trust.'
              },
              {
                icon: <Globe className="w-8 h-8" />,
                title: 'Customized Solutions',
                description: 'Every client is unique. We take a personalized approach, tailoring solutions to address specific needs and goals.'
              }
            ].map((value, index) => (
              <div key={index} className="group p-8 rounded-2xl bg-gradient-to-br from-gray-50 to-blue-50 hover:from-blue-50 hover:to-indigo-50 transition-all duration-300 hover:shadow-lg">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section id="services" className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Core Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive solutions designed to meet your unique needs across various industries
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'International Student Enrollment',
                description: 'Connect with top-notch colleges and universities in Canada for admission and enrollment.',
                features: ['Application Assistance', 'Professional Counseling', 'Documentation Support']
              },
              {
                title: 'Immigration Services',
                description: 'Expert guidance for your journey to Canada with specialized visa and permit assistance.',
                features: ['Student Visa & Study Permits', 'Permanent Residency', 'Expert Documentation']
              },
              {
                title: 'Educational Consulting',
                description: 'Comprehensive educational support for students, parents, and institutions.',
                features: ['Academic Advising', 'College Applications', 'Career Counselling']
              },
              {
                title: 'Business Planning',
                description: 'Strategic management expertise to develop and optimize your business operations.',
                features: ['Strategic Planning', 'Process Optimization', 'Performance Enhancement']
              },
              {
                title: 'Employment Services',
                description: 'Professional employment support to advance your career goals.',
                features: ['Job Search Assistance', 'Resume Writing', 'Interview Coaching']
              },
              {
                title: 'Travel & Tourism',
                description: 'Trusted travel planning and tourism services for all your travel needs.',
                features: ['Travel Planning', 'Accommodation', 'Transportation']
              }
            ].map((service, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 group">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-500">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Future?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join hundreds of successful students and businesses who have achieved their goals with our expert guidance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href='/consultations' className="bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg">
              Schedule Consultation
            </a>
            <a href='/service' className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300">
              View All Services
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center space-x-2 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-lg">S</span>
                </div>
                <span className="text-xl font-bold">SmeHub</span>
              </div>
              <p className="text-gray-400 leading-relaxed">
                Your trusted partner in educational consulting, immigration services, and comprehensive business solutions.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Student Enrollment</li>
                <li>Immigration Services</li>
                <li>Educational Consulting</li>
                <li>Business Planning</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Contact Nigeria</h4>
              <div className="space-y-3 text-gray-400">
                <div className="flex items-start">
                  <MapPin className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">2nd floor, Oludaisi Ogundare House, 19/21 Balogun street, Anifowose, Ikeja, Lagos</span>
                </div>
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+234 812 2654683</span>
                </div>
                <div className="flex items-center">
                  <Mail className="w-5 h-5 mr-2" />
                  <span>enquiry@smehub.ng</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Contact Canada</h4>
              <div className="space-y-3 text-gray-400">
                <div className="flex items-start">
                  <MapPin className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">4516 Valiant Drive, NW, Calgary, T3A 0Y1, Alberta</span>
                </div>
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+1 825 935 7540</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2025 SmeHub Consulting Limited. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;